import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';
import MessageModal from '../../components/messaging/MessageModal';

const ProfileDetailPage = () => {
  const { type, userId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        
        // Validate profile type
        const validTypes = ['startup', 'investor', 'service_provider', 'pharmaceutical'];
        if (!validTypes.includes(type)) {
          navigate('/profiles');
          return;
        }
        
        // Check if this profile type is active for the user
        const { error: userTypeError } = await supabase
          .from('user_types')
          .select('*')
          .eq('user_id', userId)
          .eq('type', type)
          .eq('is_active', true)
          .single();
          
        if (userTypeError) {
          // If error is not found, it means this profile type is not active
          if (userTypeError.code === 'PGRST116') {
            setError('This profile is currently inactive or does not exist.');
            setLoading(false);
            return;
          }
          throw userTypeError;
        }
        
        // Fetch user profile info
        const { data: userData, error: userError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', userId)
          .single();
        
        if (userError) throw userError;
        setUserProfile(userData);
        
        // Fetch specific profile data based on type
        let profileData = null;
        let productsData = [];
        
        if (type === 'startup') {
          // For startups, fetch their products
          const { data: startupProducts, error: productsError } = await supabase
            .from('products')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });
          
          if (productsError) throw productsError;
          productsData = startupProducts || [];
          profileData = { products: productsData };
        } else if (type === 'investor') {
          const { data: investorData, error: investorError } = await supabase
            .from('investor_profiles')
            .select('*')
            .eq('user_id', userId)
            .single();
            
          if (investorError) throw investorError;
          profileData = investorData;
        } else if (type === 'service_provider') {
          const { data: serviceData, error: serviceError } = await supabase
            .from('service_provider_profiles')
            .select('*')
            .eq('user_id', userId)
            .single();
            
          if (serviceError) throw serviceError;
          profileData = serviceData;
        } else if (type === 'pharmaceutical') {
          const { data: pharmaData, error: pharmaError } = await supabase
            .from('pharmaceutical_profiles')
            .select('*')
            .eq('user_id', userId)
            .single();
            
          if (pharmaError) throw pharmaError;
          profileData = pharmaData;
        }
        
        setProfile(profileData);
        setProducts(productsData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile. Please try again later.');
        setLoading(false);
      }
    };
    
    if (userId) {
      fetchProfileData();
    }
  }, [userId, type, navigate]);
  
  if (loading) {
    return <LoadingScreen message="Loading profile..." />;
  }
  
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
            <p>{error}</p>
          </div>
          <div className="flex justify-center">
            <Link to="/profiles" className="btn-primary">
              Back to Profiles
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  if (!profile || !userProfile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-6" role="alert">
            <p>Profile not found.</p>
          </div>
          <div className="flex justify-center">
            <Link to="/profiles" className="btn-primary">
              Back to Profiles
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Helper for rendering profile type label
  const getProfileLabel = (profileType) => {
    switch (profileType) {
      case 'startup': return 'Startup';
      case 'investor': return 'Investor';
      case 'service_provider': return 'Service Provider';
      case 'pharmaceutical': return 'Pharmaceutical Company';
      default: return 'Participant';
    }
  };
  
  // Render profile content based on type
  const renderProfileContent = () => {
    switch (type) {
      case 'startup':
        return (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6 bg-blue-50">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Startup Information
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Products and details for this startup.
              </p>
            </div>
            
            <div className="border-t border-gray-200">
              <div className="px-4 py-5 sm:p-6">
                <h4 className="text-md font-medium text-gray-900 mb-2">Products ({products.length})</h4>
                
                {products.length === 0 ? (
                  <p className="text-gray-500">This startup hasn't added any products yet.</p>
                ) : (
                  <div className="space-y-4">
                    {products.map((product) => (
                      <div key={product.id} className="border border-gray-200 rounded-md p-4">
                        <h5 className="font-medium text-gray-900">{product.name}</h5>
                        <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Stage</dt>
                            <dd className="mt-1 text-sm text-gray-900">{product.development_stage}</dd>
                          </div>
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Category</dt>
                            <dd className="mt-1 text-sm text-gray-900">{product.product_category}</dd>
                          </div>
                          {product.target_market && (
                            <div className="sm:col-span-2">
                              <dt className="text-sm font-medium text-gray-500">Target Market</dt>
                              <dd className="mt-1 text-sm text-gray-900">{product.target_market}</dd>
                            </div>
                          )}
                          {product.concept_description && (
                            <div className="sm:col-span-2">
                              <dt className="text-sm font-medium text-gray-500">Concept</dt>
                              <dd className="mt-1 text-sm text-gray-900">{product.concept_description}</dd>
                            </div>
                          )}
                        </div>
                        <div className="mt-3">
                          <Link to={`/products/${product.id}`} className="text-sm text-blue-600 hover:text-blue-900">
                            View Product Details
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
        
      case 'investor':
        return (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6 bg-green-50">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Investor Information
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Investment preferences and details.
              </p>
            </div>
            
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Company or Fund</dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.company_or_fund}</dd>
                </div>
                
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Investment Size</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {profile.investment_size ? `$${profile.investment_size.toLocaleString()} USD` : 'Not specified'}
                  </dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Investor Types</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {profile.investor_types && profile.investor_types.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {profile.investor_types.map((type, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {type}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'Not specified'
                    )}
                  </dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Development Stages of Interest</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {profile.development_stages && profile.development_stages.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {profile.development_stages.map((stage, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {stage}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'Not specified'
                    )}
                  </dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Investment Rounds</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {profile.investment_rounds && profile.investment_rounds.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {profile.investment_rounds.map((round, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {round}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'Not specified'
                    )}
                  </dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Investment Thesis</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                    {profile.investment_thesis || 'Not provided'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        );
        
      case 'service_provider':
        return (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6 bg-purple-50">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Service Provider Information
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Service offerings and details.
              </p>
            </div>
            
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Company</dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.company}</dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Service Types</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {profile.service_types && profile.service_types.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {profile.service_types.map((type, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {type}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'Not specified'
                    )}
                  </dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Services Description</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                    {profile.services_description || 'Not provided'}
                  </dd>
                </div>
                
                {profile.slide_url && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Additional Information</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <a 
                        href={profile.slide_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-900 flex items-center"
                      >
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        View Service Slide
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        );
        
      case 'pharmaceutical':
        return (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6 bg-red-50">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Pharmaceutical Company Information
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Company details and interests.
              </p>
            </div>
            
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Company</dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.company}</dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Company Description and Focus</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                    {profile.company_description || 'Not provided'}
                  </dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Interest Types</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {profile.interest_types && profile.interest_types.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {profile.interest_types.map((type, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {type}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'Not specified'
                    )}
                  </dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Description of Interests and Specifics</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                    {profile.interests_description || 'Not provided'}
                  </dd>
                </div>
                
                {profile.slide_url && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Additional Information</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <a 
                        href={profile.slide_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-600 hover:text-red-900 flex items-center"
                      >
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        View Company Slide
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Participant Information
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Profile details are not available.
              </p>
            </div>
          </div>
        );
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Link to="/profiles" className="text-blue-600 hover:text-blue-900 flex items-center">
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Profiles
          </Link>
        </div>
        
        <div className="bg-white shadow overflow-hidden rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center">
              {userProfile.photo_url ? (
                <div className="mr-4">
                  <img 
                    src={userProfile.photo_url} 
                    alt={userProfile.name || 'Profile'} 
                    className="h-16 w-16 rounded-full"
                  />
                </div>
              ) : (
                <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                  <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {userProfile.name && !userProfile.name.includes('@') ? userProfile.name : 'Anonymous'}
                </h2>
                <div className="flex items-center mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {getProfileLabel(type)}
                  </span>
                  {userProfile.job_title && (
                    <span className="ml-2 text-sm text-gray-500">
                      {userProfile.job_title}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
            <dl className="sm:divide-y sm:divide-gray-200">
              {userProfile.company && (
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Company</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{userProfile.company}</dd>
                </div>
              )}
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Contact</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <button
                    onClick={() => setShowMessageModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Message
                  </button>
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Member since</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {new Date(userProfile.created_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>
        
        {renderProfileContent()}
      </div>
      
      {/* Message Modal */}
      <MessageModal 
        isOpen={showMessageModal} 
        onClose={() => setShowMessageModal(false)} 
        receiverId={userId}
        receiverName={userProfile?.name || 'User'}
      />
    </div>
  );
};

export default ProfileDetailPage; 